import React, { useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StageAmount from './StageAmount';
import StageDetails from './StageDetails';
import {
  Alert,
  IconButton,
  AppBar,
  Snackbar,
  Toolbar,
} from '@mui/material';
import {
  ChevronLeft,
} from '@mui/icons-material';
import { SheetContext } from '../../context';
import { Formik, Form, FormikHelpers } from 'formik';
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import NumberKeyboard from '../../components/NumberKeyboard';
import { Expense } from '../../types';
import { AddExpenseScreenProps, ExpenseFormValues } from './types';
import '../../styles/AddExpense.scss';

export function AddExpenseScreen({
  addFn,
  rowNumber,
  categories,
  accounts,
  expense,
  isLoading,
  step,
  forceSnackOpen
}: AddExpenseScreenProps) {
  const [hasAmount, setHasAmount] = useState<boolean>(step === 2 || false);
  const [snackOpen, setSnackOpen] = useState<boolean>(forceSnackOpen || false);
  const navigate = useNavigate();

  const handleSnackClose = (): void => {
    setSnackOpen(false);
  };

  const onSubmit = (values: ExpenseFormValues, { setSubmitting }: FormikHelpers<ExpenseFormValues>): void => {
    if (!hasAmount && values.amount) {
      setHasAmount(true);
      setSubmitting(false);
    } else {
      setTimeout(async () => {
        const date = new Date(values.date);
        const now = new Date();
        date.setHours(now.getHours());
        date.setMinutes(now.getMinutes());
        date.setSeconds(now.getSeconds());

        try {
          if (values.currency === 'USDLBP') {
            await addFn({
              rowNumber: expense?.usdlbp?.usdRowNumber,
              date: date.getTime(),
              category: values.category,
              account: values.account,
              description: values.description,
              usd: parseFloat(values.amount),
            });
            await addFn({
              rowNumber: expense?.usdlbp?.lbpRowNumber,
              date: date.getTime(),
              category: values.category,
              account: values.account,
              description: values.description,
              lbp: parseInt(values.amount2 || '0'),
            });
          } else {
            await addFn({
              rowNumber,
              date: date.getTime(),
              category: values.category,
              account: values.account,
              description: values.description,
              lbp: values.currency === 'LBP' ? parseInt(values.amount) : null,
              usd: values.currency === 'USD' ? parseFloat(values.amount) : null,
            });
          }

          setSubmitting(false);
          navigate('/expenses');
        } catch (ex) {
          console.error(ex);
          setSnackOpen(true);
          setSubmitting(false);
        }
      }, 400);
    }
  };

  const navigateBack = (): void => {
    if (hasAmount) {
      setHasAmount(false);
    } else {
      navigate(-1);
    }
  };

  const Loading = (): JSX.Element => (
    <div className="add-expense-screen">
      <form>
        <AppBar color="transparent" position="static">
          <Toolbar>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 2 }}
              onClick={navigateBack}
            >
              <ChevronLeft />
            </IconButton>
          </Toolbar>
        </AppBar>
        <div className="stage-amount">
          <div className="amount-group">
            <div className="amount">
              <span className="formatted">
                <Skeleton width="50%" />
              </span>
            </div>
          </div>
          <div role="group" aria-labelledby="currency-radio-group" className="currency-radio-group">
            <label>
              <input type="radio" />
              <span>USD</span>
            </label>
            <label>
              <input type="radio" />
              <span>USD+LBP</span>
            </label>
            <label>
              <input type="radio" />
              <span>LBP</span>
            </label>
          </div>
          <NumberKeyboard />
        </div>
      </form>
    </div>
  );

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const month = (date.getMonth() + 1).toString();
    const day = date.getDate().toString();
    return `${date.getFullYear()}-${month.length === 1 ? '0' + month : month}-${day.length === 1 ? '0' + day : day}`;
  };

  const today = new Date(Date.now()).toISOString().substr(0, 10);
  const initialValues: ExpenseFormValues = {
    amount: (expense as any)?.usdlbp ? (expense as any).usdlbp.usd.toString() : (expense?.lbp || expense?.usd || 0).toString(),
    currency: (expense as any)?.usdlbp ? 'USDLBP' : (expense && !expense?.lbp ? 'USD' : 'LBP'),
    amount2: (expense as any)?.usdlbp ? (expense as any).usdlbp.lbp.toString() : '',
    date: expense ? formatDate(expense.date) : today,
    category: expense ? expense.category : '',
    account: expense ? expense.account : 'Cash',
    description: expense ? expense.description : ''
  };

  return isLoading ? <Loading /> : (
    <div className="add-expense-screen">
      <Formik
        initialValues={initialValues}
        validate={(values: ExpenseFormValues) => {
          const errors: Partial<ExpenseFormValues> = {};
          if (!values.amount || isNaN(parseFloat(values.amount)) || values.amount === '0') {
            errors.amount = 'Required';
          }
          return errors;
        }}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, submitForm, values, setFieldValue, touched, errors, handleChange }) => (
          <Form>
            <AppBar color="transparent" position="static">
              <Toolbar>
                <IconButton
                  size="large"
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  sx={{ mr: 2 }}
                  onClick={navigateBack}
                >
                  <ChevronLeft />
                </IconButton>
              </Toolbar>
            </AppBar>
            {hasAmount
              ? <StageDetails 
                  isSubmitting={isSubmitting} 
                  submitform={submitForm} 
                  values={values} 
                  setFieldValue={setFieldValue} 
                  touched={touched} 
                  errors={errors} 
                  handleChange={handleChange} 
                  categories={categories} 
                  accounts={accounts} 
                />
              : <StageAmount 
                  submitform={submitForm} 
                  values={values} 
                  setFieldValue={setFieldValue} 
                />
            }
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              key={'error-snack'}
            >
              <Alert
                onClose={handleSnackClose}
                severity="error"
                variant="filled"
                sx={{ width: '100%' }}
              >
                Could not save. Check connection and try again.
              </Alert>
            </Snackbar>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default function AddExpenseScreenWrapper(): JSX.Element {
  const ctx = useContext(SheetContext);
  const { addExpense, expenses, categories, accounts } = ctx;

  const { row } = useParams<{ row?: string }>();
  let rowNumber: number | undefined;
  let expense: Expense | undefined;

  // find matching expense based on rowNumber
  if (row && expenses) {
    rowNumber = parseInt(row);
    const match = expenses.filter((expense: Expense) => expense.rowNumber === rowNumber);
    if (match.length) {
      expense = match[0];
    }
  }
  const isLoading = Boolean(row && !expense);

  return (
    <AddExpenseScreen
      addFn={addExpense}
      categories={categories}
      accounts={accounts}
      rowNumber={rowNumber}
      expense={expense}
      isLoading={isLoading}
    />
  );
}
